/**
 * Panel de BDs personalizadas (Fase 9).
 *
 * - Lista las BDs subidas por el tenant (RLS).
 * - Drag & drop para subir CSV/JSON/SQLite/XLSX.
 * - Toggle "habilitada en chat" para activar/desactivar la BD sin borrarla.
 * - Botón borrar + reparse + ver preview.
 * - Buscador rápido cross-DB (FTS) para validar el upload.
 *
 * Sin polling agresivo: refrescamos la lista cada 3s mientras haya BDs en
 * estado `pending`/`parsing` (transitorio), y al cargar.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Switch,
  Spinner,
  Tag,
  Input,
  Tooltip,
  makeStyles,
  shorthands,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Field,
  Textarea,
} from '@fluentui/react-components';
import {
  ArrowUpload24Regular,
  Database24Regular,
  Delete20Regular,
  Search20Regular,
  ArrowClockwise20Regular,
  Eye20Regular,
} from '@fluentui/react-icons';
import {
  type CustomDb,
  type CustomDbSearchHit,
  listCustomDbs,
  uploadCustomDb,
  deleteCustomDb,
  updateCustomDb,
  reparseCustomDb,
  searchCustomDbs,
  listCustomDbRows,
} from '../api/customDbs.js';
import { useConfirm } from './ConfirmDialog.js';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-l)',
    padding: 'var(--space-xl)',
    maxWidth: '1100px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 'var(--space-m)',
  },
  title: {
    fontFamily: 'var(--font-display)',
    fontSize: 'var(--fs-600)',
    fontWeight: 600,
  },
  subtitle: {
    color: 'var(--text-secondary)',
    fontSize: 'var(--fs-300)',
  },
  uploadZone: {
    border: '2px dashed var(--border-default)',
    borderRadius: 'var(--radius-lg)',
    padding: 'var(--space-xl)',
    textAlign: 'center',
    cursor: 'pointer',
    transitionProperty: 'background-color, border-color',
    transitionDuration: '120ms',
    backgroundColor: 'var(--bg-raised)',
    ':hover': {
      ...shorthands.borderColor('var(--opus-brand-60)'),
      backgroundColor: 'rgba(125, 125, 255, 0.04)',
    },
  },
  uploadActive: {
    ...shorthands.borderColor('var(--opus-brand-60)'),
    backgroundColor: 'rgba(125, 125, 255, 0.08)',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-s)',
  },
  card: {
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-lg)',
    padding: 'var(--space-m) var(--space-l)',
    background: 'var(--bg-raised)',
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: 'var(--space-m)',
    alignItems: 'center',
  },
  cardMain: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: 0,
  },
  cardName: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-s)',
    fontWeight: 600,
    fontSize: 'var(--fs-300)',
  },
  cardMeta: {
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--fs-200)',
    color: 'var(--text-tertiary)',
    display: 'flex',
    gap: 'var(--space-m)',
    flexWrap: 'wrap',
  },
  cardActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-xs)',
  },
  empty: {
    padding: 'var(--space-xl)',
    textAlign: 'center',
    color: 'var(--text-tertiary)',
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--fs-200)',
  },
  searchBox: {
    display: 'flex',
    gap: 'var(--space-s)',
    alignItems: 'center',
    padding: 'var(--space-m)',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-lg)',
    background: 'var(--bg-raised)',
  },
  searchResults: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-xs)',
    marginTop: 'var(--space-s)',
  },
  hit: {
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-md)',
    padding: 'var(--space-s) var(--space-m)',
    fontSize: 'var(--fs-200)',
    background: 'var(--bg-default)',
  },
  hitHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    fontFamily: 'var(--font-mono)',
    color: 'var(--text-tertiary)',
    fontSize: 'var(--fs-100)',
    marginBottom: '4px',
  },
  hitJson: {
    margin: 0,
    fontSize: 'var(--fs-100)',
    fontFamily: 'var(--font-mono)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    color: 'var(--text-secondary)',
  },
  errorMsg: {
    color: 'var(--opus-danger-60, #ff6b6b)',
    fontSize: 'var(--fs-200)',
    fontFamily: 'var(--font-mono)',
  },
});

const STATUS_TAG: Record<CustomDb['parseStatus'], { label: string; color: 'outline' | 'brand' | 'filled' }> = {
  pending: { label: 'pendiente', color: 'outline' },
  parsing: { label: 'procesando…', color: 'brand' },
  ready: { label: 'lista', color: 'filled' },
  failed: { label: 'falló', color: 'outline' },
};

function formatBytes(b: number | null): string {
  if (b == null) return '—';
  if (b < 1024) return `${b}B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)}KB`;
  return `${(b / 1024 / 1024).toFixed(2)}MB`;
}

export function CustomDbsPanel() {
  const styles = useStyles();
  const confirm = useConfirm();
  const [dbs, setDbs] = useState<CustomDb[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchHits, setSearchHits] = useState<CustomDbSearchHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchMs, setSearchMs] = useState<number | null>(null);
  const [previewDb, setPreviewDb] = useState<CustomDb | null>(null);
  const [previewRows, setPreviewRows] = useState<any[] | null>(null);

  const refresh = useCallback(async () => {
    try {
      const r = await listCustomDbs();
      setDbs(r);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  // Polling: cada 3s mientras alguna BD esté en estado transitorio.
  const hasTransient = useMemo(
    () => dbs.some((d) => d.parseStatus === 'pending' || d.parseStatus === 'parsing'),
    [dbs],
  );
  useEffect(() => {
    if (!hasTransient) return;
    const t = setInterval(() => { void refresh(); }, 3000);
    return () => clearInterval(t);
  }, [hasTransient, refresh]);

  const onDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      setPendingFile(file);
      setUploadName(file.name.replace(/\.[^.]+$/, ''));
      setShowUploadDialog(true);
    }
  }, []);

  const onPickFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const onFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) {
      setPendingFile(f);
      setUploadName(f.name.replace(/\.[^.]+$/, ''));
      setShowUploadDialog(true);
    }
    e.target.value = '';
  }, []);

  const onConfirmUpload = useCallback(async () => {
    if (!pendingFile) return;
    setUploading(true);
    setError(null);
    try {
      await uploadCustomDb({
        file: pendingFile,
        name: uploadName.trim() || undefined,
        description: uploadDescription.trim() || undefined,
      });
      setShowUploadDialog(false);
      setPendingFile(null);
      setUploadName('');
      setUploadDescription('');
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setUploading(false);
    }
  }, [pendingFile, uploadName, uploadDescription, refresh]);

  const onToggle = useCallback(async (db: CustomDb, enabled: boolean) => {
    setDbs((prev) => prev.map((d) => (d.id === db.id ? { ...d, enabled } : d)));
    try {
      await updateCustomDb(db.id, { enabled });
    } catch (err) {
      setError((err as Error).message);
      void refresh();
    }
  }, [refresh]);

  const onDelete = useCallback(async (db: CustomDb) => {
    if (!(await confirm({
      title: 'Borrar base de datos',
      message: `¿Borrar "${db.name}" y todos sus ${db.rowCount} registros? Esta acción no se puede deshacer.`,
      confirmLabel: 'Borrar',
      destructive: true,
    }))) return;
    try {
      await deleteCustomDb(db.id);
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    }
  }, [refresh]);

  const onReparse = useCallback(async (db: CustomDb) => {
    try {
      await reparseCustomDb(db.id);
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    }
  }, [refresh]);

  const onSearch = useCallback(async () => {
    const q = searchQuery.trim();
    if (!q) { setSearchHits([]); setSearchMs(null); return; }
    setSearching(true);
    try {
      const res = await searchCustomDbs({ q, limit: 10 });
      setSearchHits(res.hits);
      setSearchMs(res.durationMs);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSearching(false);
    }
  }, [searchQuery]);

  const openPreview = useCallback(async (db: CustomDb) => {
    setPreviewDb(db);
    setPreviewRows(null);
    try {
      const r = await listCustomDbRows(db.id, { limit: 25 });
      setPreviewRows(r.rows.map((x) => x.payload));
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div>
          <div className={styles.title}>Mis Bases de Datos</div>
          <div className={styles.subtitle}>
            Sube tus catálogos privados (CSV, JSON, SQLite, XLSX). El asistente los consultará automáticamente cuando preguntes.
          </div>
        </div>
        <Button
          appearance="primary"
          icon={<ArrowUpload24Regular />}
          onClick={onPickFile}
        >
          Subir BD
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.json,.sqlite,.sqlite3,.db,.xlsx,.xls"
          style={{ display: 'none' }}
          onChange={onFileInputChange}
        />
      </div>

      {/* Drop zone (alternativa al botón) */}
      <div
        className={`${styles.uploadZone} ${dragActive ? styles.uploadActive : ''}`}
        onClick={onPickFile}
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
      >
        <ArrowUpload24Regular style={{ marginBottom: 8 }} />
        <div>Arrastra un archivo aquí o haz clic para seleccionar</div>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4 }}>
          CSV · JSON · SQLite · XLSX · máx. 50 MB
        </div>
      </div>

      {error && <div className={styles.errorMsg}>⚠ {error}</div>}

      {loading ? (
        <Spinner label="Cargando BDs…" />
      ) : dbs.length === 0 ? (
        <div className={styles.empty}>
          Aún no has subido ninguna base de datos. Sube tu primer catálogo para que el asistente lo use.
        </div>
      ) : (
        <div className={styles.list}>
          {dbs.map((db) => {
            const tag = STATUS_TAG[db.parseStatus];
            return (
              <div key={db.id} className={styles.card}>
                <div className={styles.cardMain}>
                  <div className={styles.cardName}>
                    <Database24Regular />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {db.name}
                    </span>
                    <Tag size="extra-small" appearance={tag.color}>{tag.label}</Tag>
                    {db.kind && <Tag size="extra-small" appearance="outline">{db.kind.toUpperCase()}</Tag>}
                  </div>
                  {db.description && (
                    <div style={{ fontSize: 'var(--fs-200)', color: 'var(--text-secondary)' }}>
                      {db.description}
                    </div>
                  )}
                  <div className={styles.cardMeta}>
                    <span>{db.rowCount} filas</span>
                    <span>{formatBytes(db.sizeBytes)}</span>
                    <span>actualizada {new Date(db.updatedAt).toLocaleString('es-MX')}</span>
                    {db.parseStatus === 'failed' && db.parseError && (
                      <span style={{ color: 'var(--opus-danger-60, #ff6b6b)' }}>error: {db.parseError.slice(0, 80)}</span>
                    )}
                  </div>
                </div>
                <div className={styles.cardActions}>
                  <Tooltip content={db.enabled ? 'Activa: el chat la consulta' : 'Desactivada'} relationship="label">
                    <Switch
                      checked={db.enabled}
                      onChange={(_, d) => void onToggle(db, d.checked)}
                      disabled={db.parseStatus !== 'ready'}
                    />
                  </Tooltip>
                  <Tooltip content="Vista previa" relationship="label">
                    <Button
                      icon={<Eye20Regular />}
                      appearance="subtle"
                      size="small"
                      onClick={() => void openPreview(db)}
                      disabled={db.parseStatus !== 'ready'}
                    />
                  </Tooltip>
                  <Tooltip content="Reprocesar" relationship="label">
                    <Button
                      icon={<ArrowClockwise20Regular />}
                      appearance="subtle"
                      size="small"
                      onClick={() => void onReparse(db)}
                    />
                  </Tooltip>
                  <Tooltip content="Borrar" relationship="label">
                    <Button
                      icon={<Delete20Regular />}
                      appearance="subtle"
                      size="small"
                      onClick={() => void onDelete(db)}
                    />
                  </Tooltip>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Buscador */}
      <div>
        <div className={styles.searchBox}>
          <Search20Regular />
          <Input
            value={searchQuery}
            onChange={(_, d) => setSearchQuery(d.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void onSearch(); }}
            placeholder="Probar búsqueda en tus BDs… (ej: 'cemento', 'proveedor X')"
            style={{ flex: 1 }}
          />
          <Button onClick={() => void onSearch()} disabled={searching || !searchQuery.trim()}>
            {searching ? <Spinner size="tiny" /> : 'Buscar'}
          </Button>
        </div>
        {searchHits.length > 0 && (
          <div className={styles.searchResults}>
            <div style={{ fontSize: 'var(--fs-200)', color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' }}>
              {searchHits.length} resultados · {searchMs}ms
            </div>
            {searchHits.map((h, i) => (
              <div key={`${h.dbId}-${h.rowIdx}-${i}`} className={styles.hit}>
                <div className={styles.hitHeader}>
                  <span>{h.dbName} · #{h.rowIdx} · {h.match}</span>
                  <span>score {h.score.toFixed(3)}</span>
                </div>
                <pre className={styles.hitJson}>
                  {JSON.stringify(h.payload, null, 2).slice(0, 500)}
                </pre>
              </div>
            ))}
          </div>
        )}
        {searchQuery && !searching && searchHits.length === 0 && searchMs != null && (
          <div className={styles.empty}>Sin resultados ({searchMs}ms)</div>
        )}
      </div>

      {/* Dialog: subir BD */}
      <Dialog open={showUploadDialog} onOpenChange={(_, d) => !uploading && setShowUploadDialog(d.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Subir base de datos</DialogTitle>
            <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {pendingFile && (
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-200)', color: 'var(--text-secondary)' }}>
                  📎 {pendingFile.name} · {formatBytes(pendingFile.size)}
                </div>
              )}
              <Field label="Nombre">
                <Input value={uploadName} onChange={(_, d) => setUploadName(d.value)} />
              </Field>
              <Field label="Descripción (opcional, ayuda al asistente a saber cuándo usarla)">
                <Textarea
                  value={uploadDescription}
                  onChange={(_, d) => setUploadDescription(d.value)}
                  rows={3}
                  placeholder="Ej: Catálogo de proveedores 2026 con teléfonos y precios negociados."
                />
              </Field>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setShowUploadDialog(false)} disabled={uploading}>
                Cancelar
              </Button>
              <Button appearance="primary" onClick={() => void onConfirmUpload()} disabled={uploading || !pendingFile}>
                {uploading ? <Spinner size="tiny" /> : 'Subir y procesar'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Dialog: preview rows */}
      <Dialog open={!!previewDb} onOpenChange={(_, d) => !d.open && setPreviewDb(null)}>
        <DialogSurface style={{ maxWidth: 900 }}>
          <DialogBody>
            <DialogTitle>{previewDb?.name}</DialogTitle>
            <DialogContent>
              {previewRows == null ? (
                <Spinner label="Cargando filas…" />
              ) : previewRows.length === 0 ? (
                <div className={styles.empty}>Sin filas</div>
              ) : (
                <div style={{ maxHeight: 400, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-200)' }}>
                    <thead>
                      <tr>
                        {Object.keys(previewRows[0] ?? {}).slice(0, 12).map((k) => (
                          <th key={k} style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--border-default)', fontFamily: 'var(--font-mono)' }}>
                            {k}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map((row, i) => (
                        <tr key={i}>
                          {Object.keys(previewRows[0] ?? {}).slice(0, 12).map((k) => (
                            <td key={k} style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-subtle)', fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-100)', color: 'var(--text-secondary)' }}>
                              {String(row?.[k] ?? '').slice(0, 80)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={() => setPreviewDb(null)}>Cerrar</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
